import { ReactNode } from 'react';
import { ScrollView, StyleProp, StyleSheet, Text, View, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts, Palette, Spacing } from '@/constants/theme';

/**
 * Outer wrapper for every onboarding step. Keeps padding consistent and
 * provides a scroll-safe container so long grids (inside/property) don't clip.
 * The progress chrome at the top is supplied by the (onboarding) layout.
 *
 * Optional `footer` prop renders a STICKY bar at the bottom of the
 * viewport. Use it for the Continue + Skip controls so they're always
 * one tap away — important on long screens like Inside / Property /
 * Lifestyle where users would otherwise have to scroll to the bottom
 * to advance. When `footer` is set the scroll area auto-pads enough
 * room so the last paragraph of content can scroll above the bar
 * instead of being permanently hidden behind it.
 */
export function Screen({
  children,
  style,
  scroll = false,
  footer,
}: {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  scroll?: boolean;
  footer?: ReactNode;
}) {
  const insets = useSafeAreaInsets();
  // Default bottom-padding clears Android's gesture nav bar. When a
  // sticky footer is mounted, scroll content needs MORE bottom padding
  // so it can scroll past the footer bar instead of being hidden
  // behind it. ~110px covers PrimaryCTA + SkipLink in their normal
  // heights with breathing room.
  const safeBottom = footer
    ? { paddingBottom: 110 + insets.bottom }
    : { paddingBottom: Math.max(Spacing.six, insets.bottom + Spacing.four) };

  const body = scroll ? (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[styles.content, safeBottom, style]}
      showsVerticalScrollIndicator={false}
      // Critical on Android: without this, tapping a dropdown suggestion
      // inside the ScrollView is consumed by the keyboard dismiss before
      // the Pressable fires (e.g. AddressAutocomplete pickSuggestion).
      keyboardShouldPersistTaps="handled">
      {children}
    </ScrollView>
  ) : (
    <View style={[styles.content, safeBottom, style]}>{children}</View>
  );

  if (!footer) return body;

  return (
    <View style={{ flex: 1 }}>
      {body}
      {/* Sticky footer — translucent sand with a hairline top border so
          the user can see content scrolling beneath without losing the
          step affordance. SafeArea-aware bottom padding so the CTA
          clears iOS home-indicator + Android gesture nav. */}
      <View
        pointerEvents="box-none"
        style={[
          styles.footer,
          { paddingBottom: Math.max(insets.bottom, 12) + 8 },
        ]}>
        <View pointerEvents="box-none" style={styles.footerInner}>
          {footer}
        </View>
      </View>
    </View>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <Text style={styles.eyebrow}>{String(children).toUpperCase()}</Text>;
}

export function H1({ children }: { children: ReactNode }) {
  return <Text style={styles.h1}>{children}</Text>;
}

export function H1Em({ children }: { children: ReactNode }) {
  return <Text style={styles.h1Em}>{children}</Text>;
}

export function WhyLine({ children }: { children: ReactNode }) {
  return <Text style={styles.why}>{children}</Text>;
}

export function GridHint({ children }: { children: ReactNode }) {
  return <Text style={styles.gridHint}>{children}</Text>;
}

export function SelCount({ count }: { count: number }) {
  return (
    <Text style={styles.selCount}>
      {count > 0 ? `${count} selected — looking good` : 'Tap what applies'}
    </Text>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  // Onboarding content centers and caps at 720px on desktop so the
  // form doesn't stretch edge-to-edge of a 1440px window — which made
  // every onboarding screen look like an oversized mobile view.
  // `alignSelf: 'center'` + `width: '100%'` + `maxWidth` is the
  // standard RN pattern that works identically on web and native
  // (mobile stays full-width since viewport < 720px).
  content: {
    flexGrow: 1,
    alignSelf: 'center',
    width: '100%',
    maxWidth: 720,
    paddingHorizontal: Spacing.five,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.six,
  },
  eyebrow: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    letterSpacing: 1.4,
    color: Palette.honeyDeep,
    marginBottom: Spacing.two,
  },
  h1: {
    fontFamily: Fonts.serif,
    fontSize: 26,
    lineHeight: 32,
    color: Palette.ink,
    letterSpacing: -0.4,
  },
  h1Em: {
    fontFamily: Fonts.serifItalic,
  },
  why: {
    marginTop: Spacing.three,
    fontSize: 14,
    lineHeight: 21,
    color: Palette.inkSoft,
    fontFamily: Fonts.sans,
  },
  gridHint: {
    marginTop: Spacing.three,
    fontSize: 13,
    color: Palette.inkFaint,
    fontFamily: Fonts.sansSemibold,
  },
  selCount: {
    marginTop: Spacing.three,
    textAlign: 'center',
    fontSize: 13,
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingTop: 10,
    backgroundColor: Palette.sand,
    borderTopWidth: 1,
    borderTopColor: Palette.line,
    alignItems: 'center',
  },
  // Inner column mirrors the content column's max-width so CTAs align
  // under the form on desktop and stay full-width on mobile.
  footerInner: {
    width: '100%',
    maxWidth: 720,
    paddingHorizontal: Spacing.five,
  },
});
